import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FileText, CheckCircle2 } from 'lucide-react';
import { Card, VStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Alert, Spinner } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{ total: number; data: T[] }>;

interface ModeleRow {
  ID_BIB: number;
  LIB_BIB: string;
}

interface DestinataireRow {
  ID_PER: number;
  LIB: string;
}

interface TypeRow {
  CD_DS: string;
  LIB_DS: string;
}

interface FusionWordResponse {
  statusCode: number;
  statusMessage: string | null;
  fileName: string;
  content: string;
}

interface EditDocumentCardProps {
  idSin: string;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
}

const GENERIC_ERROR = 'Erreur lors de la génération du document.';
const LIST_LOAD_ERROR = 'Erreur lors du chargement des listes.';

/**
 * Server-rejected-but-HTTP-200 response (`statusCode !== 0`). Carries the
 * server's `statusMessage` so `onError` can surface it verbatim — exactly as
 * the original card's inline `setSubmitError(payload?.statusMessage ?? …)`
 * did. Kept distinct from a transport-level rejection (which has no
 * `statusMessage` and falls back to the ERP envelope message or GENERIC_ERROR).
 */
class FusionWordRejection extends Error {}

/**
 * Coerce an `unknown` form value to a string. The three select fields always
 * hold a string (or empty), so this only ever sees strings at runtime; the
 * primitive guard keeps it type-safe (no `String(object)` stringification of
 * an unexpected value) and avoids `@typescript-eslint/no-base-to-string`.
 */
function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function base64ToPdfBlob(b64: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'application/pdf' });
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function EditDocumentCard({
  idSin,
  _layoutOverrides,
}: Readonly<EditDocumentCardProps>) {
  const [modeles, setModeles] = useState<ModeleRow[]>([]);
  const [destinataires, setDestinataires] = useState<DestinataireRow[]>([]);
  const [types, setTypes] = useState<TypeRow[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submittedTypeLabel, setSubmittedTypeLabel] = useState('');

  // `onSuccess` only receives the API payload, so the chosen Type's display
  // label (for the success header) is captured in a ref during `mutationFn`
  // — mirroring `CreateTaskCard`'s `lastSchedulerIdRef`.
  const lastTypeLabelRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;

    // Wrapped in an async loader so the loading/reset setState calls are not
    // synchronous statements in the effect body (react-hooks/set-state-in-
    // effect). Behaviour is identical: loading is flipped on, the three GETs
    // fire in parallel, options land together, and `isLoading` clears in the
    // finally.
    async function loadLists(): Promise<void> {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [modeleRes, destRes, typeRes] = await Promise.all([
          claimflowApiClient.get<ErpListResponse<ModeleRow>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-idbib',
            { params: { perPage: 9999 } },
          ),
          claimflowApiClient.get<ErpListResponse<DestinataireRow>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-destinataire',
            { params: { id_sin: idSin, perPage: 9999 } },
          ),
          claimflowApiClient.get<ErpListResponse<TypeRow>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdds',
            { params: { perPage: 9999 } },
          ),
        ]);
        if (cancelled) return;
        setModeles(modeleRes.data.data.data ?? []);
        setDestinataires(destRes.data.data.data ?? []);
        setTypes(typeRes.data.data.data ?? []);
      } catch {
        if (cancelled) return;
        setLoadError(LIST_LOAD_ERROR);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadLists();

    return () => {
      cancelled = true;
    };
  }, [idSin]);

  const isFormReady = !isLoading && !loadError;

  // Declarative field config — labels, order, validation and placeholders live
  // here (off the JSX) so an admin can rename / hide / reorder / tooltip the
  // three fields per tenant. Keys (`modeleId` / `destId` / `cdDs`) are the
  // stable contract and equal the IGA `fusion-word` body params; never rename.
  const baseConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'edit-document',
      endpoint: {
        erpEndpoint: '/claimflow/erp/registries/veos/endpoints/fusion-word',
        method: 'POST',
      },
      fields: {
        modeleId: {
          type: 'select',
          label: 'Modèle',
          order: 1,
          validation: { required: true },
          options: {
            items: modeles.map((m) => ({
              value: String(m.ID_BIB),
              label: m.LIB_BIB,
            })),
            searchable: true,
            placeholder: 'Sélectionner un modèle',
          } as SelectFieldOptions,
        },
        destId: {
          type: 'select',
          label: 'Destinataire',
          order: 2,
          validation: { required: true },
          options: {
            items: destinataires.map((d) => ({
              value: String(d.ID_PER),
              label: d.LIB,
            })),
            searchable: true,
            placeholder: 'Sélectionner un destinataire',
          } as SelectFieldOptions,
        },
        cdDs: {
          type: 'select',
          label: 'Type',
          order: 3,
          validation: { required: true },
          options: {
            items: types.map((t) => ({
              value: t.CD_DS,
              label: t.LIB_DS,
            })),
            searchable: true,
            placeholder: 'Sélectionner un type',
          } as SelectFieldOptions,
        },
      },
      actions: {
        submit: { visible: true, label: 'Générer le document' },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 1, gap: 'sm' },
    }),
    [modeles, destinataires, types],
  );

  usePublishLayoutConfig(
    'claimflow.editDocumentCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Éditer un document'),
  );

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      const modeleIdVal = asString(data.modeleId);
      const cdDsVal = asString(data.cdDs);
      const destIdVal = asString(data.destId);

      const selectedModele = modeles.find(
        (m) => String(m.ID_BIB) === modeleIdVal,
      );
      const selectedType = types.find((t) => t.CD_DS === cdDsVal);
      // Capture the chosen Type's display label for the success header — the
      // `onSuccess` callback only receives the API payload.
      lastTypeLabelRef.current = selectedType?.LIB_DS ?? '';

      // The Origami ERP proxy unwraps the outer `data` envelope before
      // forwarding to IGA; IGA's fusion-word body itself is wrapped in
      // `params`, hence the double nesting. IGA is strict on string
      // types — every numeric id (`_ARCHIVE_ENTITY_ID`, `idBib`,
      // `idDest`) is stringified before being sent.
      const response = await claimflowApiClient.post<
        ErpProxyResponse<FusionWordResponse>
      >('/claimflow/erp/registries/veos/endpoints/fusion-word', {
        data: {
          params: {
            _ARCHIVE_ENTITY_ID: String(idSin),
            _ARCHIVE_SERVICE: 'SINISTRE',
            _ARCHIVE_TYPE_DOC: cdDsVal,
            _ARCHIVE_TYPE_ENTITY: 'SINISTRE',
            catAdr: 'PRINCENVOI',
            docName: selectedModele?.LIB_BIB ?? '',
            idBib: modeleIdVal,
            idDest: destIdVal,
            idSign: '',
          },
        },
      });

      const result = response.data.data;
      // A server-rejected-but-HTTP-200 response (statusCode !== 0) is converted
      // to a thrown error so `useEntityForm`'s error path (→ onError) handles
      // it uniformly with HTTP failures. It carries the server `statusMessage`
      // (preferred), falling back to GENERIC_ERROR — matching the original
      // inline `setSubmitError(payload?.statusMessage ?? GENERIC_ERROR)`.
      if (!result || result.statusCode !== 0) {
        throw new FusionWordRejection(result?.statusMessage ?? GENERIC_ERROR);
      }
      return result;
    },
    [idSin, modeles, types],
  );

  const handleSuccess = useCallback((response: unknown) => {
    const payload = response as FusionWordResponse;
    // Decode + auto-download only after success is confirmed (kept in
    // onSuccess, never mutationFn, so a rejected submit never downloads).
    const blob = base64ToPdfBlob(payload.content);
    triggerDownload(blob, payload.fileName);
    setSubmittedTypeLabel(lastTypeLabelRef.current);
    setSubmitted(true);
  }, []);

  const handleError = useCallback((err: unknown) => {
    // statusCode !== 0 rejections carry the server statusMessage verbatim;
    // transport-level failures fall back to the ERP error envelope message,
    // then GENERIC_ERROR — preserving the original two-branch behaviour.
    if (err instanceof FusionWordRejection) {
      setSubmitError(err.message);
      return;
    }
    setSubmitError(extractErpErrorMessage(err) ?? GENERIC_ERROR);
  }, []);

  const form = useEntityForm({
    config,
    mutationFn,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  // Reproduce the original "disabled until all three selects are filled"
  // gate. The integrated form makes required-field validation Zod's job, but
  // `form.isValid` is derived from the *errors* map, which is empty (and thus
  // "valid") before any field is touched — so it cannot drive a disabled-on-
  // mount button. We therefore gate on the live values of the three stable
  // keys, exactly as the hand-laid `allFilled` did.
  const allFilled =
    asString(form.values.modeleId) !== '' &&
    asString(form.values.destId) !== '' &&
    asString(form.values.cdDs) !== '';

  if (submitted) {
    return (
      <Card key="submitted" id={actionAnchorId('claimflow.editDocumentCard')}>
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Éditer un document', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            — {submittedTypeLabel}
          </Text>
        </Card.Header>
        <Card.Content>
          <VStack gap={4}>
            <EntityForm config={config} form={form}>
              <EntityForm.Fields />
            </EntityForm>
            <Button
              variant="primary"
              onClick={() => {
                setSubmitted(false);
                setSubmitError(null);
              }}
              className="w-full"
            >
              Générer le document
            </Button>
          </VStack>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card key="form" id={actionAnchorId('claimflow.editDocumentCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
        action={isLoading ? <Spinner size="sm" /> : undefined}
      >
        <Icon icon={FileText} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Éditer un document', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        {loadError && (
          <div className="mb-4">
            <Alert variant="error">{loadError}</Alert>
          </div>
        )}
        {submitError && (
          <div className="mb-4">
            <Alert variant="error">{submitError}</Alert>
          </div>
        )}
        <EntityForm config={config} form={form}>
          <EntityForm.Fields />
          {config.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                loading={form.isSubmitting}
                disabled={form.isSubmitting || !isFormReady || !allFilled}
                className="w-full"
              >
                {config.actions?.submit?.label ?? 'Générer le document'}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

function extractErpErrorMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const envelope = data as {
    error?: { message?: unknown };
    raw_response?: { message?: unknown };
  };
  if (typeof envelope.error?.message === 'string' && envelope.error.message.length > 0) {
    return envelope.error.message;
  }
  if (
    typeof envelope.raw_response?.message === 'string' &&
    envelope.raw_response.message.length > 0
  ) {
    return envelope.raw_response.message;
  }
  return undefined;
}
